import React, { memo, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { escape } from 'lodash';
import { Button, Card, CardBody } from 'reactstrap';
import { switchMap } from 'rxjs/operators';
import { RegionAPI } from '@business/api/region';
import { Blocs } from '@business/blocs';
import { DealResponse } from '@business/entities/deal';
import { RegionId, RegionResponse } from '@business/entities/region';
import { handleMessage, requestMessage } from '@business/messages';
import { UserService } from '@business/services';
import { Session } from '@modules/auth/session';
import { showConfirmation } from '@modules/common';
import { BaseBloc, useBloc } from '@core/utils/bloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { resolve } from '@core/utils/ioc';
import { R } from '@core/utils/r';
import { InfoBanner } from '@modules/common/InfoBanner';
import { geoJsonToPaths } from './geoJson';
import { MapTerritory, TerritoryMap } from './TerritoryMap';
import { territoryColor } from './territoryColors';
import { openAddEditTerritoryModal } from '@modules/settings/SettingsPage/Components/TerritoriesTab/AddEditTerritoryModal';

class DealTerritoryMapBloc extends BaseBloc {
  private readonly regionBloc = resolve(Blocs.region);
  readonly regions$ = this.regionBloc.regions$;
  fetchRegions = this.regionBloc.fetchRegions;
}

// The overlay mounts every tab up front; Google bills each map it draws, so
// the map (and its territories) load only once this tab is actually on screen.
function useShownOnce(ref: RefObject<HTMLElement>) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (shown || !ref.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setShown(true);
        observer.disconnect();
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, shown]);
  return shown;
}

const NO_FOCUS = { id: null, version: 0 };

export interface DealTerritoryMapTabProps {
  deal: DealResponse;
  /** Reload the deal after its territory changed here. */
  onChanged(): void;
}

/** Where the deal's address sits among the territories: a pin, its territory (or overlap candidates) highlighted. */
export const DealTerritoryMapTab = memo(({ deal, onChanged }: DealTerritoryMapTabProps) => {
  const canManage = useMemo(() => !!Session.user && UserService.isAdmin(Session.user), []);
  const ref = useRef<HTMLDivElement>(null);
  const shown = useShownOnce(ref);
  const bloc = useBloc(DealTerritoryMapBloc);
  const regions = useObservable(bloc.regions$, []);

  useEffect(() => {
    if (!shown) return;
    const subscription = bloc.fetchRegions().subscribe();
    return () => subscription.unsubscribe();
  }, [bloc, shown]);

  const pin = useMemo(
    () => (deal.regionLat != null && deal.regionLng != null ? { lat: Number(deal.regionLat), lng: Number(deal.regionLng) } : null),
    [deal.regionLat, deal.regionLng],
  );

  const highlightIds = useMemo(
    () => (deal.regionId ? [R.toString(deal.regionId)] : (deal.regionCandidates || []).map(c => R.toString(c.id))),
    [deal.regionCandidates, deal.regionId],
  );

  const territories: MapTerritory[] = useMemo(
    () =>
      regions.map((r, index) => ({
        id: R.toString(r.id),
        name: r.name,
        color: territoryColor(index),
        paths: geoJsonToPaths(r.geoJson),
        reps: (r.salesReps || []).map(rep => `${rep.firstName} ${rep.lastName}`),
      })),
    [regions],
  );

  // Admins settle the territory right on the map, saved as chosen by hand.
  const assign = useCallback(
    (regionId: RegionId, name: string, message: string) =>
      showConfirmation('Set Territory', message.replace('{name}', `<b>${escape(name)}</b>`))
        .pipe(
          switchMap(() => RegionAPI.setDealRegion(deal.id, regionId)),
          handleMessage({ type: requestMessage('set_deal_region') }),
        )
        .subscribe(() => onChanged()),
    [deal.id, onChanged],
  );

  const onSelectTerritory = useCallback(
    (id: string | null) => {
      if (!id || (deal.regionId && R.toString(deal.regionId) === id)) return;
      const territory = territories.find(t => t.id === id);
      if (territory) assign(id, territory.name, "Set this deal's territory to {name}? It will show as set manually.");
    },
    [assign, deal.regionId, territories],
  );

  // A new territory drawn from here only ever claims this one deal; other deals keep theirs.
  const addTerritoryHere = useCallback(
    () =>
      openAddEditTerritoryModal({
        focusPoint: pin,
        onSaved: (region: RegionResponse) => assign(region.id, region.name, 'Put this deal in the new territory {name}?'),
      }),
    [assign, pin],
  );

  let notice: string | null = null;
  if (!pin) {
    if (deal.regionStatus === 'no_address') notice = "This deal's organization has no address on file, so there's nothing to place on the map.";
    else if (deal.regionStatus === 'not_located') notice = "This deal's address couldn't be found on the map.";
    // Checking again would replace a hand-picked territory, so don't suggest it here.
    else if (deal.regionStatus === 'manual') notice = "This deal's address couldn't be placed on the map; its territory was chosen by hand.";
    else notice = "This deal's address hasn't been placed on the map yet. An admin can use the refresh icon next to Territory to check it.";
  }

  return (
    <Card>
      <CardBody>
        <div ref={ref}>
          {notice ? (
            <InfoBanner noMargin>{notice}</InfoBanner>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-2" style={{ gap: 8 }}>
                <div className="text-muted font-size-12">
                  <i className="bx bx-map-pin mr-1" />
                  {deal.regionAddress}
                  {canManage && <span className="ml-2">· Click a territory to set it as this deal&apos;s territory.</span>}
                </div>
                {canManage && (
                  <Button type="button" size="sm" color="primary" outline onClick={addTerritoryHere}>
                    <i className="bx bx-plus mr-1" />
                    Add territory here
                  </Button>
                )}
              </div>
              {shown && (
                <TerritoryMap
                  territories={territories}
                  selectedId={null}
                  highlightIds={highlightIds}
                  pin={pin}
                  focus={NO_FOCUS}
                  onSelect={canManage ? onSelectTerritory : undefined}
                  height={480}
                />
              )}
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
});
